import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ScanLine, Radio, Wifi, WifiOff, Volume2, VolumeX, Tag,
  Shirt, Trash2, ArrowLeft, Users, Package, AlertTriangle,
  CheckCircle2, XCircle, Settings2, Plug, PlugZap, Loader2,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { useNavigate } from 'react-router-dom';
import { findByTagsRfid } from '@/services/registroPrendasRfid.service';
import useSerialReader from '@/hooks/useSerialReader';
import { useAuth } from '@/contexts/AuthContext';

const ConteoPrendasRFIDPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Mode: 'usb' for HID reader, 'antenna' for TCP antenna
  const [mode, setMode] = useState('usb');

  // USB HID scanning state
  const [isScanning, setIsScanning] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [tagBuffer, setTagBuffer] = useState('');
  const rfidInputRef = useRef(null);

  // Antenna config state (persisted in localStorage)
  const [antennaConfig, setAntennaConfig] = useState(() => {
    try {
      const saved = localStorage.getItem('rfid_antenna_config');
      return saved ? JSON.parse(saved) : { baudRate: 115200, minReads: 2, power: 20, buzzer: true };
    } catch { return { baudRate: 115200, minReads: 2, power: 20, buzzer: true }; }
  });
  const [antennaStarting, setAntennaStarting] = useState(false);

  const BAUD_RATES = [9600, 19200, 38400, 57600, 115200];

  // Web Serial reader hook (reads COM port directly from browser)
  const serialReader = useSerialReader({
    minReads: antennaConfig.minReads || 2,
    onNewTag: () => playBeep(),
  });

  // Collected tags (raw list from scanner)
  const [scannedTags, setScannedTags] = useState([]);

  // Results after processing
  const [results, setResults] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Focus input when scanning (USB mode)
  useEffect(() => {
    if (isScanning && rfidInputRef.current && mode === 'usb') {
      rfidInputRef.current.focus();
    }
  }, [isScanning, mode]);

  // Save antenna config to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('rfid_antenna_config', JSON.stringify(antennaConfig));
    } catch {}
  }, [antennaConfig]);

  // Sync serial reader tags → scannedTags (antenna mode)
  useEffect(() => {
    if (serialReader.tags.length > 0 && mode === 'antenna') {
      setScannedTags((prev) => {
        const existing = new Set(prev);
        const newTags = serialReader.tags.filter((t) => !existing.has(t));
        if (newTags.length > 0) return [...newTags, ...prev];
        return prev;
      });
    }
  }, [serialReader.tags, mode]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (serialReader.isConnected) serialReader.disconnect();
    };
  }, []);

  const playBeep = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 800;
      osc.type = 'sine';
      gain.gain.value = 0.2;
      osc.start();
      osc.stop(ctx.currentTime + 0.1);
    } catch (e) {
      // Audio not available
    }
  }, [soundEnabled]);

  const playDoubleBeep = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 600;
      osc.type = 'sine';
      gain.gain.value = 0.2;
      osc.start();
      osc.stop(ctx.currentTime + 0.08);
      setTimeout(() => {
        try {
          const ctx2 = new (window.AudioContext || window.webkitAudioContext)();
          const osc2 = ctx2.createOscillator();
          const gain2 = ctx2.createGain();
          osc2.connect(gain2);
          gain2.connect(ctx2.destination);
          osc2.frequency.value = 600;
          osc2.type = 'sine';
          gain2.gain.value = 0.2;
          osc2.start();
          osc2.stop(ctx2.currentTime + 0.08);
        } catch (e) {}
      }, 120);
    } catch (e) {}
  }, [soundEnabled]);

  // Handle tag input from RFID reader
  const handleRfidInput = useCallback((e) => {
    if (e.key === 'Enter' && tagBuffer.trim()) {
      const tag = tagBuffer.trim();
      setTagBuffer('');

      // Check if already scanned (duplicate)
      if (scannedTags.includes(tag)) {
        playDoubleBeep();
        toast({
          title: 'Tag duplicado',
          description: `"${tag}" ya fue escaneado`,
        });
      } else {
        playBeep();
        setScannedTags((prev) => [tag, ...prev]);
      }

      // Re-focus input
      setTimeout(() => rfidInputRef.current?.focus(), 50);
    }
  }, [tagBuffer, scannedTags, playBeep, playDoubleBeep, toast]);

  // Process all scanned tags — send to backend
  const handleProcessTags = useCallback(async () => {
    if (scannedTags.length === 0) {
      toast({ title: 'Sin tags', description: 'No hay tags escaneados para procesar', variant: 'destructive' });
      return;
    }

    setIsProcessing(true);
    try {
      const response = await findByTagsRfid(scannedTags);
      const { registrados = [], noRegistrados = [] } = response?.data || {};

      // Group registered items by client
      const byClient = {};
      registrados.forEach((item) => {
        const clientName = item.cliente?.nombre || 'Sin cliente';
        const clientId = item.cliente?.id || 'unknown';
        if (!byClient[clientId]) {
          byClient[clientId] = {
            nombre: clientName,
            id: clientId,
            prendas: [],
            byType: {},
          };
        }
        byClient[clientId].prendas.push(item);
        const typeName = item.tipoPrenda?.nombre || 'Sin tipo';
        byClient[clientId].byType[typeName] = (byClient[clientId].byType[typeName] || 0) + 1;
      });

      setResults({
        registrados,
        noRegistrados,
        byClient: Object.values(byClient),
        totalScanned: scannedTags.length,
      });

      toast({
        title: 'Conteo completado',
        description: `${registrados.length} registradas, ${noRegistrados.length} sin registrar de ${scannedTags.length} tags`,
      });
    } catch (error) {
      toast({ title: 'Error', description: 'Error al procesar los tags', variant: 'destructive' });
    } finally {
      setIsProcessing(false);
    }
  }, [scannedTags, toast]);

  // Antenna handlers (Web Serial API — reads directly from browser)
  const handleStartAntenna = async () => {
    if (!serialReader.isSupported) {
      toast({ title: 'No soportado', description: 'Tu navegador no soporta Web Serial API. Usa Chrome o Edge.', variant: 'destructive' });
      return;
    }
    setAntennaStarting(true);
    try {
      const ok = await serialReader.connect(antennaConfig.baudRate);
      if (ok) {
        // Apply power and buzzer settings after connecting
        if (antennaConfig.power) await serialReader.setPower(antennaConfig.power);
        await serialReader.setBuzzer(antennaConfig.buzzer !== false);
        toast({ title: 'Lector conectado', description: `Leyendo a ${antennaConfig.baudRate} baud...` });
      }
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'No se pudo conectar', variant: 'destructive' });
    } finally {
      setAntennaStarting(false);
    }
  };

  const handleStopAntenna = async () => {
    try {
      await serialReader.disconnect();
      toast({ title: 'Lector desconectado', description: `${serialReader.tags.length} tags recolectados` });
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  // Reset everything
  const handleReset = async () => {
    if (serialReader.isReading) await handleStopAntenna();
    serialReader.clearTags();
    setScannedTags([]);
    setResults(null);
    setIsScanning(false);
  };

  // Remove a single tag
  const removeTag = (tag) => {
    setScannedTags((prev) => prev.filter((t) => t !== tag));
    if (results) setResults(null); // Clear results since data changed
  };

  // Add tag manually
  const [manualTag, setManualTag] = useState('');
  const handleAddManualTag = () => {
    if (!manualTag.trim()) return;
    const tag = manualTag.trim();
    if (scannedTags.includes(tag)) {
      toast({ title: 'Duplicado', description: 'Este tag ya fue escaneado' });
    } else {
      setScannedTags((prev) => [tag, ...prev]);
      if (results) setResults(null);
    }
    setManualTag('');
  };

  return (
    <div className="container mx-auto p-4 space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4"
      >
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate('/rfid')}>
            <ArrowLeft className="h-4 w-4 mr-1" /> RFID
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <ScanLine className="h-7 w-7 text-emerald-600" />
              Conteo de Prendas RFID
            </h1>
            <p className="text-muted-foreground mt-1">
              Escanea prendas con la antena RFID y cuenta por cliente
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSoundEnabled(!soundEnabled)}
          >
            {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          </Button>
          <Button variant="outline" size="sm" onClick={handleReset} disabled={scannedTags.length === 0}>
            <Trash2 className="h-4 w-4 mr-1" /> Limpiar todo
          </Button>
        </div>
      </motion.div>

      {/* Mode Selector */}
      <div className="flex gap-2">
        <Button
          variant={mode === 'usb' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setMode('usb')}
        >
          <Plug className="h-4 w-4 mr-1" /> Lector USB (Escritorio)
        </Button>
        <Button
          variant={mode === 'antenna' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setMode('antenna')}
          className={mode === 'antenna' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
        >
          <PlugZap className="h-4 w-4 mr-1" /> Antena RS232 (Largo Alcance)
        </Button>
        {scannedTags.length > 0 && (
          <Button
            variant="default"
            size="sm"
            onClick={handleProcessTags}
            disabled={isProcessing}
            className="ml-auto bg-emerald-600 hover:bg-emerald-700"
          >
            <ScanLine className="h-4 w-4 mr-1" />
            {isProcessing ? 'Procesando...' : `Procesar Conteo (${scannedTags.length})`}
          </Button>
        )}
      </div>

      {/* USB HID Mode */}
      {mode === 'usb' && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="border-2 border-dashed border-blue-300 bg-blue-50/50 dark:bg-blue-950/20 dark:border-blue-800">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2">
                    {isScanning ? (
                      <Wifi className="h-5 w-5 text-green-500 animate-pulse" />
                    ) : (
                      <WifiOff className="h-5 w-5 text-gray-400" />
                    )}
                    <span className="font-medium text-sm">
                      {isScanning ? 'Lector USB activo — Pasa las prendas una a una' : 'Lectura USB pausada'}
                    </span>
                    {isScanning && (
                      <Badge className="bg-green-600 text-white animate-pulse">
                        {scannedTags.length} tags
                      </Badge>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant={isScanning ? 'destructive' : 'default'}
                      size="sm"
                      onClick={() => {
                        setIsScanning(!isScanning);
                        if (!isScanning) setTimeout(() => rfidInputRef.current?.focus(), 100);
                      }}
                    >
                      <Radio className="h-4 w-4 mr-1" />
                      {isScanning ? 'Detener' : 'Iniciar Lectura'}
                    </Button>
                  </div>
                  <input
                    ref={rfidInputRef}
                    type="text"
                    value={tagBuffer}
                    onChange={(e) => setTagBuffer(e.target.value)}
                    onKeyDown={handleRfidInput}
                    className={`w-full px-3 py-2 rounded-md border text-sm font-mono ${
                      isScanning
                        ? 'border-blue-400 bg-blue-50 dark:bg-blue-950/30 focus:ring-blue-500'
                        : 'border-gray-300 bg-gray-50 dark:bg-gray-900'
                    } focus:outline-none focus:ring-2`}
                    placeholder={isScanning ? 'Escaneando tags RFID...' : 'Click en "Iniciar Lectura"'}
                    readOnly={!isScanning}
                    autoComplete="off"
                  />
                </div>
                <div className="w-full md:w-72 space-y-2">
                  <span className="font-medium text-sm text-muted-foreground">Agregar tag manual</span>
                  <div className="flex gap-2">
                    <Input
                      value={manualTag}
                      onChange={(e) => setManualTag(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddManualTag()}
                      placeholder="Tag RFID..."
                      className="font-mono"
                    />
                    <Button size="sm" onClick={handleAddManualTag}>+</Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Antenna RS232 Mode (Web Serial API — reads directly from browser) */}
      {mode === 'antenna' && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          <Card className="border-2 border-dashed border-emerald-300 bg-emerald-50/50 dark:bg-emerald-950/20 dark:border-emerald-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Settings2 className="h-5 w-5 text-emerald-600" />
                Lector RFID UHF (RS232 / Web Serial)
              </CardTitle>
              <CardDescription>
                Conecta tu lector RFID UHF (UHF-105 / S4A) por cable RS232 con adaptador USB-Serial. El navegador detectará el puerto automáticamente.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Connection row */}
              <div className="flex flex-wrap gap-3 items-end">
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Baud Rate</Label>
                  <select
                    value={antennaConfig.baudRate}
                    onChange={(e) => setAntennaConfig((p) => ({ ...p, baudRate: parseInt(e.target.value) }))}
                    disabled={serialReader.isReading}
                    className="w-40 h-9 rounded-md border border-input bg-background px-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                  >
                    {BAUD_RATES.map((rate) => (
                      <option key={rate} value={rate}>{rate}</option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-2">
                  {!serialReader.isReading ? (
                    <Button
                      size="sm"
                      onClick={handleStartAntenna}
                      disabled={antennaStarting}
                      className="h-9 bg-emerald-600 hover:bg-emerald-700"
                    >
                      {antennaStarting ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <PlugZap className="h-4 w-4 mr-1" />
                      )}
                      Seleccionar Puerto y Leer
                    </Button>
                  ) : (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleStopAntenna}
                      className="h-9"
                    >
                      <WifiOff className="h-4 w-4 mr-1" /> Detener
                    </Button>
                  )}
                </div>
              </div>

              {/* Web Serial not supported warning */}
              {!serialReader.isSupported && (
                <div className="flex items-center gap-2 text-sm p-2 rounded-md bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                  <AlertTriangle className="h-4 w-4" />
                  <span>Tu navegador no soporta Web Serial API. Usa <strong>Chrome</strong> o <strong>Edge</strong> en escritorio.</span>
                </div>
              )}

              {/* Error */}
              {serialReader.error && (
                <div className="flex items-center gap-2 text-sm p-2 rounded-md bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                  <XCircle className="h-4 w-4" />
                  <span>{serialReader.error}</span>
                </div>
              )}

              {/* Advanced config: Power, Min Reads, Buzzer */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2 border-t border-dashed border-emerald-200 dark:border-emerald-800">
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Potencia TX ({antennaConfig.power || 20} dBm)</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">1</span>
                    <input
                      type="range"
                      min="1"
                      max="30"
                      value={antennaConfig.power || 20}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        setAntennaConfig((p) => ({ ...p, power: val }));
                        if (serialReader.isReading) serialReader.setPower(val);
                      }}
                      className="flex-1 h-2 accent-emerald-600"
                    />
                    <span className="text-xs text-muted-foreground">30</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Menor potencia = menos alcance = menos ruido</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Lecturas mínimas ({antennaConfig.minReads || 2}x)</Label>
                  <select
                    value={antennaConfig.minReads || 2}
                    onChange={(e) => {
                      const val = parseInt(e.target.value);
                      setAntennaConfig((p) => ({ ...p, minReads: val }));
                      serialReader.setMinReads(val);
                    }}
                    className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    {[1, 2, 3, 5, 8, 10].map((n) => (
                      <option key={n} value={n}>{n}x — {n === 1 ? 'Sin filtro' : n <= 3 ? 'Filtro bajo' : n <= 5 ? 'Filtro medio' : 'Filtro alto'}</option>
                    ))}
                  </select>
                  <p className="text-xs text-muted-foreground">Un tag debe leerse N veces para confirmar</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Buzzer del lector</Label>
                  <Button
                    variant={antennaConfig.buzzer !== false ? 'default' : 'outline'}
                    size="sm"
                    className="w-full h-9"
                    onClick={async () => {
                      const newVal = antennaConfig.buzzer === false;
                      setAntennaConfig((p) => ({ ...p, buzzer: newVal }));
                      if (serialReader.isReading) {
                        await serialReader.setBuzzer(newVal);
                        toast({ title: newVal ? 'Buzzer activado' : 'Buzzer desactivado' });
                      }
                    }}
                  >
                    {antennaConfig.buzzer !== false ? (
                      <><Volume2 className="h-4 w-4 mr-1" /> Buzzer ON</>
                    ) : (
                      <><VolumeX className="h-4 w-4 mr-1" /> Buzzer OFF</>
                    )}
                  </Button>
                  <p className="text-xs text-muted-foreground">Sonido del lector al detectar tag</p>
                </div>
              </div>

              {/* Reading status */}
              {serialReader.isReading && (
                <div className="flex items-center gap-3 p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-md">
                  <Wifi className="h-5 w-5 text-emerald-600 animate-pulse" />
                  <div className="flex-1">
                    <span className="font-medium text-sm text-emerald-800 dark:text-emerald-400">
                      Lector leyendo — {antennaConfig.baudRate} baud
                    </span>
                    <p className="text-xs text-emerald-600 dark:text-emerald-500">
                      Acerca las prendas al lector. Los tags aparecerán automáticamente.
                      {serialReader.pendingCount > 0 && (
                        <span className="ml-1 font-medium">({serialReader.pendingCount} pendiente{serialReader.pendingCount > 1 ? 's' : ''} de confirmar)</span>
                      )}
                    </p>
                  </div>
                  <Badge className="bg-emerald-600 text-white text-lg px-3">
                    {scannedTags.length}
                  </Badge>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Scanned Tags List */}
      {scannedTags.length > 0 && !results && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Tag className="h-5 w-5 text-blue-500" />
                Tags escaneados: {scannedTags.length}
              </CardTitle>
              <CardDescription>
                Presiona "Procesar Conteo" para identificar las prendas y agrupar por cliente
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
                {scannedTags.map((tag, idx) => (
                  <Badge key={idx} variant="outline" className="font-mono text-xs gap-1 pr-1">
                    {tag}
                    <button
                      onClick={() => removeTag(tag)}
                      className="ml-1 hover:text-red-500 transition-colors"
                    >
                      <XCircle className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Results */}
      <AnimatePresence>
        {results && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="space-y-6"
          >
            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-3xl font-bold text-blue-600">{results.totalScanned}</div>
                  <div className="text-xs text-muted-foreground mt-1">Tags Escaneados</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-3xl font-bold text-emerald-600">{results.registrados.length}</div>
                  <div className="text-xs text-muted-foreground mt-1">Prendas Registradas</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-3xl font-bold text-amber-600">{results.noRegistrados.length}</div>
                  <div className="text-xs text-muted-foreground mt-1">Tags Sin Registrar</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-3xl font-bold text-purple-600">{results.byClient.length}</div>
                  <div className="text-xs text-muted-foreground mt-1">Clientes Detectados</div>
                </CardContent>
              </Card>
            </div>

            {/* By Client Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Desglose por Cliente
                </CardTitle>
                <CardDescription>
                  Prendas agrupadas por cliente y tipo
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {results.byClient.length === 0 ? (
                  <p className="text-muted-foreground text-sm text-center py-4">
                    No se encontraron prendas registradas
                  </p>
                ) : (
                  results.byClient
                    .sort((a, b) => b.prendas.length - a.prendas.length)
                    .map((client) => (
                      <motion.div
                        key={client.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="border rounded-lg p-4 bg-card"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div className="bg-primary/10 p-2 rounded-lg">
                              <Users className="h-5 w-5 text-primary" />
                            </div>
                            <div>
                              <h3 className="font-semibold">{client.nombre}</h3>
                              <p className="text-xs text-muted-foreground">
                                {client.prendas.length} prenda{client.prendas.length !== 1 ? 's' : ''}
                              </p>
                            </div>
                          </div>
                          <Badge className="text-lg px-3 py-1 bg-emerald-600 text-white">
                            {client.prendas.length}
                          </Badge>
                        </div>

                        {/* Type breakdown */}
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(client.byType)
                            .sort(([, a], [, b]) => b - a)
                            .map(([type, count]) => (
                              <Badge key={type} variant="outline" className="gap-1">
                                <Shirt className="h-3 w-3" />
                                {type}: <strong>{count}</strong>
                              </Badge>
                            ))}
                        </div>

                        {/* Individual tags (collapsible) */}
                        <details className="mt-3">
                          <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                            Ver tags individuales ({client.prendas.length})
                          </summary>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {client.prendas.map((p) => (
                              <Badge key={p.tagRfid} variant="secondary" className="font-mono text-xs">
                                {p.tagRfid}
                              </Badge>
                            ))}
                          </div>
                        </details>
                      </motion.div>
                    ))
                )}
              </CardContent>
            </Card>

            {/* Unregistered Tags */}
            {results.noRegistrados.length > 0 && (
              <Card className="border-amber-300 dark:border-amber-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-amber-600">
                    <AlertTriangle className="h-5 w-5" />
                    Tags Sin Registrar ({results.noRegistrados.length})
                  </CardTitle>
                  <CardDescription>
                    Estos tags fueron detectados pero no están en el sistema. Regístralos en "Registro de Prendas".
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {results.noRegistrados.map((tag) => (
                      <Badge key={tag} variant="outline" className="font-mono text-xs text-amber-600 border-amber-400">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={() => navigate('/rfid/registro')}
                  >
                    <Tag className="h-4 w-4 mr-1" /> Ir a Registro de Prendas
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Scanned tags raw list */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Tag className="h-4 w-4" />
                  Todos los tags escaneados ({scannedTags.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                  {scannedTags.map((tag, idx) => {
                    const isRegistered = results.registrados.some((r) => r.tagRfid === tag);
                    return (
                      <Badge
                        key={idx}
                        variant="outline"
                        className={`font-mono text-xs gap-1 ${
                          isRegistered ? 'text-green-600 border-green-300' : 'text-amber-600 border-amber-300'
                        }`}
                      >
                        {isRegistered ? (
                          <CheckCircle2 className="h-3 w-3" />
                        ) : (
                          <AlertTriangle className="h-3 w-3" />
                        )}
                        {tag}
                      </Badge>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ConteoPrendasRFIDPage;
